import { useState } from 'react'
import { motion } from 'framer-motion'
import { Send, CheckCircle, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { InnerHero } from '@/components/sections'
import { Prefooter, Footer } from '@/components/layout'
import { LocalizedLink } from '@/components/LocalizedLink'

interface FormState {
  posizione: string
  nome: string
  cognome: string
  telefono: string
  email: string
  privacy: boolean
}

export function LavoraConNoiPage() {
  const { t } = useTranslation('common')

  const positions = [
    {
      title: t('lavoraConNoi.position1Title'),
      location: t('lavoraConNoi.position1Location'),
      type: t('lavoraConNoi.position1Type'),
      description: t('lavoraConNoi.position1Desc'),
    },
    {
      title: t('lavoraConNoi.position2Title'),
      location: t('lavoraConNoi.position2Location'),
      type: t('lavoraConNoi.position2Type'),
      description: t('lavoraConNoi.position2Desc'),
    },
    {
      title: t('lavoraConNoi.position3Title'),
      location: t('lavoraConNoi.position3Location'),
      type: t('lavoraConNoi.position3Type'),
      description: t('lavoraConNoi.position3Desc'),
    },
    {
      title: t('lavoraConNoi.position4Title'),
      location: t('lavoraConNoi.position4Location'),
      type: t('lavoraConNoi.position4Type'),
      description: t('lavoraConNoi.position4Desc'),
    },
    {
      title: t('lavoraConNoi.position5Title'),
      location: t('lavoraConNoi.position5Location'),
      type: t('lavoraConNoi.position5Type'),
      description: t('lavoraConNoi.position5Desc'),
    },
    {
      title: t('lavoraConNoi.position6Title'),
      location: t('lavoraConNoi.position6Location'),
      type: t('lavoraConNoi.position6Type'),
      description: t('lavoraConNoi.position6Desc'),
    },
    {
      title: t('lavoraConNoi.position7Title'),
      location: t('lavoraConNoi.position7Location'),
      type: t('lavoraConNoi.position7Type'),
      description: t('lavoraConNoi.position7Desc'),
    },
    {
      title: t('lavoraConNoi.position8Title'),
      location: t('lavoraConNoi.position8Location'),
      type: t('lavoraConNoi.position8Type'),
      description: t('lavoraConNoi.position8Desc'),
    },
  ]

  const positionOptions = [
    t('lavoraConNoi.spontaneous'),
    ...positions.map((p) => p.title),
  ]

  const [form, setForm] = useState<FormState>({
    posizione: '',
    nome: '',
    cognome: '',
    telefono: '',
    email: '',
    privacy: false,
  })
  const [submitted, setSubmitted] = useState(false)

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((s) => ({ ...s, [key]: value }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !form.posizione ||
      !form.nome.trim() ||
      !form.cognome.trim() ||
      !form.telefono.trim() ||
      !form.email.trim() ||
      !form.privacy
    ) {
      return
    }
    setSubmitted(true)
  }

  return (
    <main>
      <InnerHero
        eyebrow={t('lavoraConNoi.heroEyebrow')}
        title={t('lavoraConNoi.heroTitle')}
        titleItalic={t('lavoraConNoi.heroTitleItalic')}
        body={t('lavoraConNoi.heroBody')}
        breadcrumbs={[
          { label: t('nav.home'), href: '/it' },
          { label: t('lavoraConNoi.breadcrumb') },
        ]}
      />

      {/* Positions */}
      <section className="bg-cream py-20 md:py-28">
        <div className="nashi-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9 }}
          >
            <span className="nashi-eyebrow">{t('lavoraConNoi.positionsEyebrow')}</span>
            <h2
              className="mt-6 font-display text-bruno mb-12"
              style={{
                fontSize: 'clamp(1.875rem, 3vw, 2.5rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">{t('lavoraConNoi.positionsTitle1')}</span>
              <span className="block italic font-light text-argan-deep">
                {t('lavoraConNoi.positionsTitle2')}
              </span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {positions.map((pos, idx) => (
              <motion.div
                key={pos.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: idx * 0.06 }}
                className="p-6 border border-bruno/10 bg-white/60 hover:border-bruno/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-display text-bruno text-lg">
                    {pos.title}
                  </h3>
                  <span className="text-[10px] uppercase tracking-[0.16em] text-argan-deep font-medium">
                    {pos.type}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-bruno-muted mb-3">
                  <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {pos.location}
                </div>
                <p className="text-sm text-bruno-soft leading-relaxed">
                  {pos.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="bg-cream-soft py-20 md:py-28">
        <div className="nashi-container max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9 }}
          >
            <span className="nashi-eyebrow">{t('lavoraConNoi.formEyebrow')}</span>
            <h2
              className="mt-6 font-display text-bruno mb-12"
              style={{
                fontSize: 'clamp(1.875rem, 3vw, 2.5rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">{t('lavoraConNoi.formTitle1')}</span>
              <span className="block italic font-light text-argan-deep">
                {t('lavoraConNoi.formTitle2')}
              </span>
            </h2>
          </motion.div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="bg-white px-8 py-14 md:px-14 md:py-20 text-center border border-bruno/10"
            >
              <CheckCircle
                className="mx-auto h-10 w-10 text-argan-deep mb-6"
                strokeWidth={1.5}
              />
              <span className="font-display italic text-argan-deep text-sm tracking-[0.32em] uppercase">
                {t('lavoraConNoi.thanksEyebrow')}
              </span>
              <h3
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(2rem, 3.5vw, 3rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.02em',
                }}
              >
                <span className="block">{t('lavoraConNoi.thanksTitle1')}</span>
                <span className="block italic font-light text-argan-deep">
                  {t('lavoraConNoi.thanksTitle2')}
                </span>
              </h3>
              <p className="mt-6 max-w-md mx-auto text-bruno-soft leading-relaxed">
                {t('lavoraConNoi.thanksBody')}
              </p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9 }}
              onSubmit={handleSubmit}
              className="space-y-6"
              noValidate
            >
              <div>
                <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                  {t('lavoraConNoi.labelPosition')}{' '}
                  <span className="text-argan-deep">*</span>
                </label>
                <div className="relative">
                  <select
                    required
                    value={form.posizione}
                    onChange={(e) => update('posizione', e.target.value)}
                    className="nashi-input w-full appearance-none pr-10 cursor-pointer"
                  >
                    <option value="" disabled>
                      {t('lavoraConNoi.selectPlaceholder')}
                    </option>
                    {positionOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-bruno-muted"
                  >
                    ↓
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                    {t('lavoraConNoi.labelFirstName')} <span className="text-argan-deep">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.nome}
                    onChange={(e) => update('nome', e.target.value)}
                    placeholder={t('lavoraConNoi.placeholderFirstName')}
                    className="nashi-input w-full"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                    {t('lavoraConNoi.labelLastName')} <span className="text-argan-deep">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.cognome}
                    onChange={(e) => update('cognome', e.target.value)}
                    placeholder={t('lavoraConNoi.placeholderLastName')}
                    className="nashi-input w-full"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                    {t('lavoraConNoi.labelPhone')} <span className="text-argan-deep">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={form.telefono}
                    onChange={(e) => update('telefono', e.target.value)}
                    placeholder={t('lavoraConNoi.placeholderPhone')}
                    className="nashi-input w-full"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                    {t('lavoraConNoi.labelEmail')} <span className="text-argan-deep">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder={t('lavoraConNoi.placeholderEmail')}
                    className="nashi-input w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                  {t('lavoraConNoi.labelCv')}
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="nashi-input w-full py-2.5 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-[11px] file:uppercase file:tracking-[0.16em] file:bg-bruno file:text-cream hover:file:bg-argan-deep file:transition-colors"
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-bruno-muted">
                  {t('lavoraConNoi.cvHint')}
                </p>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                  {t('lavoraConNoi.labelCoverLetter')}
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="nashi-input w-full py-2.5 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-[11px] file:uppercase file:tracking-[0.16em] file:bg-bruno file:text-cream hover:file:bg-argan-deep file:transition-colors"
                  />
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  required
                  checked={form.privacy}
                  onChange={(e) => update('privacy', e.target.checked)}
                  className="mt-1 accent-bruno w-3.5 h-3.5 shrink-0"
                />
                <span className="text-[12px] leading-relaxed text-bruno-soft">
                  {t('lavoraConNoi.privacyPrefix')}{' '}
                  <LocalizedLink
                    to="privacy"
                    className="underline underline-offset-2 hover:text-argan-deep transition-colors"
                  >
                    {t('footer.privacy')}
                  </LocalizedLink>{' '}
                  {t('lavoraConNoi.privacySuffix')}
                </span>
              </label>

              <div className="pt-4">
                <button
                  type="submit"
                  className="group inline-flex items-center gap-3 bg-bruno text-cream font-medium uppercase tracking-[0.28em] text-[11px] px-10 py-4 hover:bg-argan-deep transition-colors"
                >
                  {t('lavoraConNoi.submit')}
                  <Send
                    className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5"
                    strokeWidth={1.75}
                  />
                </button>
              </div>
            </motion.form>
          )}
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
